import type { Geometry, WindowSnapshot } from "../workspace/schema.js";

export const TILE_GAP = 8;

/**
 * Tile a room into a near-square grid, ordered by `tileIndex` (§6.1). The last
 * row stretches to fill, so there is never a ragged gap.
 */
export function tileRects(count: number, area: { w: number; h: number }): Geometry[] {
  if (count <= 0) return [];
  const columns = Math.ceil(Math.sqrt(count));
  const rows = Math.ceil(count / columns);
  const height = (area.h - TILE_GAP * (rows + 1)) / rows;

  const rects: Geometry[] = [];
  for (let index = 0; index < count; index++) {
    const row = Math.floor(index / columns);
    const column = index % columns;
    const inRow = Math.min(columns, count - row * columns);
    const width = (area.w - TILE_GAP * (inRow + 1)) / inRow;
    rects.push({
      x: TILE_GAP + column * (width + TILE_GAP),
      y: TILE_GAP + row * (height + TILE_GAP),
      w: Math.max(1, width),
      h: Math.max(1, height),
    });
  }
  return rects;
}

/** Windows in tile order, paired with the rect each one occupies. */
export function tiled(
  windows: readonly WindowSnapshot[],
  area: { w: number; h: number },
): { window: WindowSnapshot; rect: Geometry }[] {
  const ordered = [...windows].sort(
    (a, b) => a.tileIndex - b.tileIndex || a.id.localeCompare(b.id),
  );
  const rects = tileRects(ordered.length, area);
  return ordered.map((window, index) => ({
    window,
    rect: rects[index] ?? { x: 0, y: 0, w: area.w, h: area.h },
  }));
}

export function windowAtPoint(
  placed: readonly { window: WindowSnapshot; rect: Geometry }[],
  x: number,
  y: number,
): WindowSnapshot | null {
  for (const entry of placed) {
    const { rect } = entry;
    if (x >= rect.x && x <= rect.x + rect.w && y >= rect.y && y <= rect.y + rect.h) {
      return entry.window;
    }
  }
  return null;
}
